import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import NumbersIcon from '@mui/icons-material/Numbers';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RestoreIcon from '@mui/icons-material/Restore';
import RuleIcon from '@mui/icons-material/Rule';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

import DynamicForm from '../../components/ui/dynamicForm/dynamicForm.component';
import JobFormRuleForm from '../../components/ui/jobFormRuleForm/jobFormRuleForm.component';
import CustomModal from '../../components/ui/modal/modal.component';
import StatusTransitionActionForm from '../../components/ui/statusTransitionActionForm/statusTransitionActionForm.component';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import { NUMERATION_SCHEMA_ENDPOINT } from '../../consts';
import { useAuthContext, useHasPermission } from '../../contexts/auth';
import { Action } from '../../types/toolbar';
import { ContractAutomationItem } from '../../types/contracts';
import { AddPermissionGroup } from '../../workspaces/tenantConfig/addPermissionGroup/addPermissionGroup.component';
import { AddUser } from '../../workspaces/tenantConfig/addUser/addUser';
import { ChangeSubscription } from '../../workspaces/tenantConfig/changeSubscription/changeSubscription.component';
import { CounterPriorityConflictsModal } from '../../workspaces/tenantConfig/counterPriorityConflictsModal/counterPriorityConflictsModal.component';
import { EditTenant } from '../../workspaces/tenantConfig/editTenant/editTenant.component';
import { EditTenantSettings } from '../../workspaces/tenantConfig/editTenantSettings/editTenantSettings.component';
import { buildNumerationSchemaFieldAttributes } from '../../workspaces/tenantConfig/numerationSchema.config';
import { TENANT_ROUTES } from '../../workspaces/tenantConfig/tenantConfig.routes';
import { SETTINGS_SECTIONS, SettingsSectionKey } from '../../workspaces/tenantConfig/tenantSettings.config';
import { TenantState } from '../../workspaces/tenantConfig/tenantState/tenantState.component';

/** The page whose actions the toolbar is currently showing. */
type TenantSection =
  | 'overview'
  | 'subscription'
  | 'users'
  | 'groups'
  | SettingsSectionKey
  | 'clientPortal'
  | 'jobStatusActions'
  | 'jobFormRules';

/**
 * Longest path first, so a nested page ("/tenant/settings/invoicing") is
 * recognised before the section it sits in ("/tenant/settings").
 */
const SECTION_PATHS: [TenantSection, string][] = [
  ['numbering', TENANT_ROUTES.numbering],
  ['invoicing', TENANT_ROUTES.invoicing],
  ['reminders', TENANT_ROUTES.reminders],
  ['clientPortal', TENANT_ROUTES.clientPortal],
  ['general', TENANT_ROUTES.settings],
  ['jobStatusActions', TENANT_ROUTES.jobStatusActions],
  ['jobFormRules', TENANT_ROUTES.jobFormRules],
  ['automation', TENANT_ROUTES.automation],
  ['subscription', TENANT_ROUTES.subscription],
  ['users', TENANT_ROUTES.users],
  ['groups', TENANT_ROUTES.permissionGroups],
];

const sectionFromPath = (pathname: string): TenantSection =>
  SECTION_PATHS.find(([, path]) => pathname === path || pathname.startsWith(`${path}/`))?.[0] ??
  'overview';

/** Which modal is open, if any - only ever one at a time. */
type ModalName =
  | 'editTenant'
  | 'tenantState'
  | 'changeSubscription'
  | 'addUser'
  | 'addGroup'
  | 'editSettings'
  | 'addSchema'
  | 'addTransitionAction'
  | 'addJobFormRule';

export const TenantActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const section = sectionFromPath(pathname);
  const { currentUser } = useAuthContext();
  const tenant = currentUser?.tenant;

  const [openModal, setOpenModal] = useState<ModalName | null>(null);
  const [statusMode, setStatusMode] = useState<'deactivate' | 'reactivate'>('deactivate');
  const [counterPriorityConflicts, setCounterPriorityConflicts] = useState<ContractAutomationItem[]>([]);

  const canChangeTenant = useHasPermission('app.change_tenant');
  const canChangeSubscription = useHasPermission('payment.add_tenantsubscription');
  const canAddUser = useHasPermission('users.add_user');
  const canAddGroup = useHasPermission('auth.add_group');
  const canChangeSettings = useHasPermission('config.change_tenantsettings');
  const canAddSchema = useHasPermission('settlement.add_numerationschema');
  const canAddTransitionAction = useHasPermission('service.add_jobstatustransitionaction');
  const canAddJobFormRule = useHasPermission('service.add_jobformrule');

  const closeModal = () => setOpenModal(null);
  const closeAndGo = (path: string) => {
    closeModal();
    navigate(path);
  };

  const openStatusModal = (mode: 'deactivate' | 'reactivate') => {
    setStatusMode(mode);
    setOpenModal('tenantState');
  };

  /** The section whose settings the "Edit settings" modal is currently editing. */
  const editableSettingsSection: SettingsSectionKey | null =
    section === 'general' ||
    section === 'numbering' ||
    section === 'invoicing' ||
    section === 'reminders' ||
    section === 'automation'
      ? section
      : null;

  const sectionedActions: { section: TenantSection; action: Action }[] = [
    {
      section: 'overview',
      action: {
        icon: <ApartmentIcon />,
        label: t('Edit workspace'),
        onClick: () => setOpenModal('editTenant'),
        disabled: !canChangeTenant,
      },
    },
    ...(tenant
      ? [
          {
            section: 'overview' as TenantSection,
            action: tenant.is_active
              ? {
                  icon: <PowerSettingsNewIcon />,
                  label: t('Deactivate workspace'),
                  onClick: () => openStatusModal('deactivate'),
                  disabled: !canChangeTenant,
                  color: 'var(--errorColor)',
                }
              : {
                  icon: <RestoreIcon />,
                  label: t('Undo deactivation'),
                  onClick: () => openStatusModal('reactivate'),
                  disabled: !canChangeTenant,
                },
          },
        ]
      : []),
    {
      section: 'subscription',
      action: {
        icon: <AutorenewIcon />,
        label: t('Change subscription'),
        onClick: () => setOpenModal('changeSubscription'),
        disabled: !canChangeSubscription,
      },
    },
    {
      section: 'users',
      action: {
        icon: <PersonAddIcon />,
        label: t('Add user'),
        onClick: () => setOpenModal('addUser'),
        disabled: !canAddUser,
      },
    },
    {
      section: 'groups',
      action: {
        icon: <SecurityIcon />,
        label: t('Add permission group'),
        onClick: () => setOpenModal('addGroup'),
        disabled: !canAddGroup,
      },
    },
    // One "Edit settings" action per settings page, each opening a form with
    // only that page's fields.
    ...(Object.keys(SETTINGS_SECTIONS) as SettingsSectionKey[]).map((key) => ({
      section: key as TenantSection,
      action: {
        icon: <SettingsIcon />,
        label: t(SETTINGS_SECTIONS[key].editLabel),
        onClick: () => setOpenModal('editSettings'),
        disabled: !canChangeSettings,
      },
    })),
    {
      section: 'numbering',
      action: {
        icon: <NumbersIcon />,
        label: t('Add numeration schema'),
        onClick: () => setOpenModal('addSchema'),
        disabled: !canAddSchema,
      },
    },
    {
      section: 'jobStatusActions',
      action: {
        icon: <SyncAltIcon />,
        label: t('Add status transition action'),
        onClick: () => setOpenModal('addTransitionAction'),
        disabled: !canAddTransitionAction,
      },
    },
    {
      section: 'jobFormRules',
      action: {
        icon: <RuleIcon />,
        label: t('Add job form rule'),
        onClick: () => setOpenModal('addJobFormRule'),
        disabled: !canAddJobFormRule,
      },
    },
  ];

  const actions: Action[] = sectionedActions
    .filter((entry) => entry.section === section)
    .map((entry) => entry.action);

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={openModal === 'editTenant'}
        setOpened={closeModal}
        title={t('Edit workspace')}
        content={<EditTenant tenant={tenant} onSuccess={() => closeAndGo(TENANT_ROUTES.overview)} />}
      />
      <CustomModal
        opened={openModal === 'tenantState'}
        setOpened={closeModal}
        title={statusMode === 'deactivate' ? t('Deactivate workspace') : t('Undo deactivation')}
        content={
          <TenantState
            tenant={tenant}
            mode={statusMode}
            onSuccess={() => closeAndGo(TENANT_ROUTES.overview)}
          />
        }
      />
      <CustomModal
        opened={openModal === 'changeSubscription'}
        setOpened={closeModal}
        title={t('Change subscription')}
        content={
          <ChangeSubscription
            tenant={tenant}
            onSuccess={() => closeAndGo(TENANT_ROUTES.subscription)}
          />
        }
      />
      <CustomModal
        opened={openModal === 'addUser'}
        setOpened={closeModal}
        title={t('Add user')}
        content={<AddUser onSuccess={() => closeAndGo(TENANT_ROUTES.users)} />}
      />
      <CustomModal
        opened={openModal === 'addGroup'}
        setOpened={closeModal}
        title={t('Add permission group')}
        content={<AddPermissionGroup onSuccess={() => closeAndGo(TENANT_ROUTES.permissionGroups)} />}
      />
      {editableSettingsSection && (
        <CustomModal
          opened={openModal === 'editSettings'}
          setOpened={closeModal}
          title={t(SETTINGS_SECTIONS[editableSettingsSection].editLabel)}
          content={
            <EditTenantSettings
              section={editableSettingsSection}
              onSuccess={(data) => {
                closeModal();
                // Contracts pinned to another provider now disagree with the
                // new tenant default; the user is shown which ones.
                const conflicts: ContractAutomationItem[] = data?.counter_priority_conflicts || [];
                setCounterPriorityConflicts(conflicts);
              }}
            />
          }
        />
      )}
      <CustomModal
        opened={openModal === 'addSchema'}
        setOpened={closeModal}
        title={t('Add numeration schema')}
        content={
          <DynamicForm
            url={NUMERATION_SCHEMA_ENDPOINT}
            onSuccess={() => {
              window.dispatchEvent(new Event('numerationSchemasUpdated'));
              closeAndGo(TENANT_ROUTES.numbering);
            }}
            fieldAttributes={buildNumerationSchemaFieldAttributes(t)}
          />
        }
      />
      <CustomModal
        opened={openModal === 'addTransitionAction'}
        setOpened={closeModal}
        title={t('Add status transition action')}
        content={
          <StatusTransitionActionForm
            onSuccess={() => {
              window.dispatchEvent(new Event('statusTransitionActionsUpdated'));
              closeAndGo(TENANT_ROUTES.jobStatusActions);
            }}
          />
        }
      />
      <CustomModal
        opened={openModal === 'addJobFormRule'}
        setOpened={closeModal}
        title={t('Add job form rule')}
        content={
          <JobFormRuleForm
            onSuccess={() => {
              window.dispatchEvent(new Event('jobFormRulesUpdated'));
              closeAndGo(TENANT_ROUTES.jobFormRules);
            }}
          />
        }
      />
      <CounterPriorityConflictsModal
        opened={counterPriorityConflicts.length > 0}
        setOpened={() => setCounterPriorityConflicts([])}
        contracts={counterPriorityConflicts}
      />
    </>
  );
};
